import type {
  TimerListener,
  TimerShotEvent,
  TimerSource,
  TimerStatus,
} from '@flm9/scoring';

/**
 * Phone-as-shot-timer (beta). A drop-in `TimerSource` that uses the
 * phone's microphone for shot-onset detection and the speaker for the
 * start beep. Intended only for solo self-training — multiple shooters
 * on neighbouring lanes will trigger false positives the anti-echo
 * window can't filter out.
 *
 * Detection runs in an inline `AudioWorklet` (see WORKLET_SOURCE
 * below) so the timestamp is taken on the audio thread, not the main
 * thread. We disable echoCancellation/autoGainControl/noiseSuppression
 * because each of them mangles the impulse: AGC compresses the peak,
 * noiseSuppression smears the rise time, and echo-cancellation can
 * eat the entire transient. Chrome ties AGC to echoCancellation, so
 * even on default-AGC-off browsers we still flip echoCancellation off
 * to defeat it.
 */

export interface AppliedAudioConstraints {
  echoCancellation: boolean;
  autoGainControl: boolean;
  noiseSuppression: boolean;
}

export interface PhoneTimerCalibration {
  /** EWMA noise-floor estimate captured during the ambient phase (RMS, full-scale). */
  noiseFloor: number;
  /** Absolute floor below which a peak is treated as noise. */
  aAbs: number;
  /** Speaker -> mic round-trip latency, subtracted from every shot timestamp. */
  audioRoundTripMs: number;
  /** When the calibration was captured (Date.now()). */
  capturedAt: number;
}

export interface PhoneTimerConfig {
  /** Sensitivity gain over the ambient noise floor. 8x ≈ 18 dB. */
  kRel: number;
  /** Absolute lower bound on frame RMS for a detection to register. */
  aAbs: number;
  /**
   * Anti-echo / minimum split. Real shot timers (RangeTech, Shotbud,
   * PACT, CED) all converge on roughly 110 ms — the fastest legitimate
   * IPSC splits are ~130 ms, so 110 ms is the safe floor.
   */
  minSplitMs: number;
  /** EWMA per-quantum smoothing factor for the running noise floor. */
  noiseFloorAlpha: number;
  /** High-pass cutoff (Hz). Kills HVAC / wind / movement rumble. */
  highPassHz: number;
  /** Time after the start beep before the detector arms. */
  preBeepArmDelayMs: number;
  /** Fixed start delay (ms). Ignored when `startDelayMode === 'random'`. */
  startDelayMs: number;
  /** 'random' picks 1-4 s like the SG-Timer's 0xFFFF default. */
  startDelayMode: 'fixed' | 'random';
  /** Optional shot cap. When reached, `start()` auto-stops. */
  expectedShots: number | null;
  /** Optional run length (ms). Auto-stops when elapsed. 0 = unlimited. */
  timeLimitMs: number;
  /** Subtracted from every shot timestamp; comes from calibration. */
  audioRoundTripMs: number;
  /** Starting value of the worklet's EWMA noise floor; comes from calibration. */
  initialNoiseFloor: number;
  /** After a shot, re-arm only once rms < rearmRatio · trigger (anti-reverb hysteresis). */
  rearmRatio: number;
}

export const PHONE_TIMER_DEFAULTS: PhoneTimerConfig = {
  kRel: 8,
  aAbs: 0.05,
  minSplitMs: 110,
  // ≈1-second EWMA at 48 kHz / 128-sample render quantum (375 quanta/s).
  noiseFloorAlpha: 0.0027,
  highPassHz: 400,
  preBeepArmDelayMs: 250,
  startDelayMs: 3000,
  startDelayMode: 'random',
  expectedShots: null,
  timeLimitMs: 0,
  audioRoundTripMs: 0,
  initialNoiseFloor: 0.001,
  rearmRatio: 0.5,
};

export interface PhoneTimerLevel {
  /** Latest frame RMS (full-scale). */
  rms: number;
  /** EWMA noise-floor estimate. */
  noiseFloor: number;
  /** Effective trigger threshold: max(noiseFloor*kRel, aAbs). */
  threshold: number;
  /**
   * AudioContext time at which the frame was sampled (seconds).
   * Same clock as the `scheduledAtCtxTime` returned from
   * `playCalibrationTone`.
   */
  t: number;
  /**
   * Loudest frame RMS since the previous level report (max-hold — level
   * reports are decimated to ~30 Hz, a short impulse would otherwise be
   * missed). Use this for peak / round-trip measurements.
   */
  peakRms: number;
  /** AudioContext time (s) of the first frame in that window at ≥ half of `peakRms` (the peak's onset). */
  peakT: number;
}

interface WorkletInMsgLevel {
  type: 'level';
  rms: number;
  noiseFloor: number;
  t: number;
  peakRms: number;
  peakT: number;
}
interface WorkletInMsgShot {
  type: 'shot';
  t: number;
  peakRms: number;
}
type WorkletInMsg = WorkletInMsgLevel | WorkletInMsgShot;

/**
 * Audio thread code. Self-contained — `AudioWorkletGlobalScope` cannot
 * import modules. Loaded via a Blob URL so we don't need a build-time
 * worklet asset. `currentTime` is provided by the global scope and
 * comes from the same clock as `AudioContext.currentTime` on the main
 * thread, so timestamps line up directly.
 *
 * KEEP IN SYNC with the shared detector `ShotOnsetDetector` in
 * packages/scoring/src/onsetDetector.ts (unit-tested there; used by the
 * React Native app). `process()` below mirrors its `decide()` step on
 * audio-clock seconds instead of sample indices; the high-pass is the
 * BiquadFilterNode ahead of this node.
 */
const WORKLET_SOURCE = `
class ShotDetectorProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const o = options.processorOptions || {};
    this.kRel = o.kRel ?? 8;
    this.aAbs = o.aAbs ?? 0.05;
    this.minSplitMs = o.minSplitMs ?? 110;
    this.noiseFloorAlpha = o.noiseFloorAlpha ?? 0.0027;
    this.noiseFloor = o.initialNoiseFloor ?? 0.001;
    this.rearmRatio = o.rearmRatio ?? 0.5;
    this.armed = false;
    this.latched = false;
    this.armAtTime = 0;
    this.lastShotTime = -Infinity;
    // Throttle level posts to ~30 Hz to keep main-thread message
    // traffic sane (raw quantum rate is 375 Hz at 48 kHz / 128).
    // Each report also carries the window's max RMS (peakRms) and the
    // onset time of that peak (peakT) so short impulses survive.
    this.levelStride = 12;
    this.levelCounter = 0;
    this.winRms = new Float32Array(this.levelStride);
    this.winT = new Float64Array(this.levelStride);
    this.port.onmessage = (e) => {
      const m = e.data;
      if (!m) return;
      switch (m.type) {
        case 'config':
          if (typeof m.kRel === 'number') this.kRel = m.kRel;
          if (typeof m.aAbs === 'number') this.aAbs = m.aAbs;
          break;
        case 'arm':
          // Detection (and floor learning) stays off until atTime — set
          // to the end of the start beep + arm delay, so the beep can't
          // trigger or inflate the noise floor.
          this.armed = true;
          this.latched = false;
          this.armAtTime = typeof m.atTime === 'number' ? m.atTime : currentTime;
          this.lastShotTime = -Infinity;
          break;
        case 'disarm':
          this.armed = false;
          this.latched = false;
          break;
        case 'reset-noise-floor':
          this.noiseFloor = m.value ?? 0.001;
          break;
      }
    };
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch || ch.length === 0) return true;

    let sumSq = 0;
    for (let i = 0; i < ch.length; i++) {
      const s = ch[i];
      sumSq += s * s;
    }
    const rms = Math.sqrt(sumSq / ch.length);

    this.winRms[this.levelCounter] = rms;
    this.winT[this.levelCounter] = currentTime;
    this.levelCounter = (this.levelCounter + 1) % this.levelStride;
    if (this.levelCounter === 0) {
      let peakRms = 0;
      for (let i = 0; i < this.levelStride; i++) {
        if (this.winRms[i] > peakRms) peakRms = this.winRms[i];
      }
      // Onset of the peak: first frame in the window at >= half of it.
      let peakT = currentTime;
      for (let i = 0; i < this.levelStride; i++) {
        if (this.winRms[i] >= peakRms * 0.5) {
          peakT = this.winT[i];
          break;
        }
      }
      this.port.postMessage({
        type: 'level',
        rms,
        noiseFloor: this.noiseFloor,
        t: currentTime,
        peakRms,
        peakT,
      });
    }

    // --- mirrors ShotOnsetDetector.decide() (packages/scoring) ---
    const trigger = Math.max(this.noiseFloor * this.kRel, this.aAbs);
    if (!this.armed) {
      // Learn the floor while idle so the run starts well-calibrated.
      this.learn(rms);
      return true;
    }
    if (currentTime < this.armAtTime) return true;

    if (this.latched && rms < trigger * this.rearmRatio) this.latched = false;

    const elapsedMs = (currentTime - this.lastShotTime) * 1000;
    if (elapsedMs < this.minSplitMs) return true;

    if (!this.latched && rms > trigger) {
      this.lastShotTime = currentTime;
      this.latched = true;
      this.port.postMessage({ type: 'shot', t: currentTime, peakRms: rms });
      return true;
    }
    if (rms <= trigger) this.learn(rms);
    return true;
  }
  learn(rms) {
    this.noiseFloor =
      this.noiseFloor * (1 - this.noiseFloorAlpha) + rms * this.noiseFloorAlpha;
  }
}
registerProcessor('shot-detector', ShotDetectorProcessor);
`;

const PHONE_TIMER_LS_KEY = 'flm9.referee.phoneTimer.calibration.v1';

export function loadPhoneTimerCalibration(): PhoneTimerCalibration | null {
  try {
    const raw = window.localStorage.getItem(PHONE_TIMER_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PhoneTimerCalibration>;
    if (
      typeof parsed.noiseFloor === 'number' &&
      typeof parsed.aAbs === 'number' &&
      typeof parsed.audioRoundTripMs === 'number'
    ) {
      return {
        noiseFloor: parsed.noiseFloor,
        aAbs: parsed.aAbs,
        audioRoundTripMs: parsed.audioRoundTripMs,
        capturedAt: parsed.capturedAt ?? Date.now(),
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function savePhoneTimerCalibration(cal: PhoneTimerCalibration): void {
  try {
    window.localStorage.setItem(PHONE_TIMER_LS_KEY, JSON.stringify(cal));
  } catch {
    // Storage may be unavailable — non-fatal.
  }
}

export function micAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices === 'object' &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof window !== 'undefined' &&
    typeof window.AudioContext === 'function'
  );
}

export class PhoneTimerSource implements TimerSource {
  readonly id = 'phone';
  readonly name = 'Phone Timer (Beta)';

  private _status: TimerStatus = { state: 'idle', startedAt: null, shots: [] };
  private listeners = new Set<TimerListener>();
  private levelListeners = new Set<(level: PhoneTimerLevel) => void>();

  private ctx: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private highPassNode: BiquadFilterNode | null = null;
  private appliedConstraints: AppliedAudioConstraints | null = null;

  /** AudioContext.currentTime captured the moment the start beep fires. */
  private startBeepCtxTime: number | null = null;
  private armDelayTimer: number | null = null;
  private armWorkletTimer: number | null = null;
  private timeLimitTimer: number | null = null;

  private cfg: PhoneTimerConfig;

  constructor(opts: Partial<PhoneTimerConfig> = {}) {
    this.cfg = { ...PHONE_TIMER_DEFAULTS, ...opts };
  }

  isConnected(): boolean {
    return this.ctx !== null && this.workletNode !== null && this.mediaStream !== null;
  }

  /**
   * Acquire mic permission, build the audio graph, install the worklet.
   * Idempotent — calling on an already-connected source returns the
   * cached constraint readback.
   *
   * Must be invoked from a user gesture; iOS Safari refuses to resume
   * a suspended AudioContext and refuses `getUserMedia` otherwise.
   */
  async connect(): Promise<{ applied: AppliedAudioConstraints }> {
    if (this.isConnected()) {
      return { applied: this.appliedConstraints ?? defaultApplied() };
    }
    if (!micAvailable()) throw new Error('mic_unavailable');

    const ctx = new AudioContext({ latencyHint: 'interactive', sampleRate: 48000 });
    try {
      return await this.buildGraph(ctx);
    } catch (err) {
      // Permission denied, worklet load failure, … — don't leak the context
      // (browsers cap the number of live AudioContexts).
      try {
        await ctx.close();
      } catch {
        // Already closed.
      }
      throw err;
    }
  }

  private async buildGraph(ctx: AudioContext): Promise<{ applied: AppliedAudioConstraints }> {
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    const blob = new Blob([WORKLET_SOURCE], { type: 'application/javascript' });
    const blobUrl = URL.createObjectURL(blob);
    try {
      await ctx.audioWorklet.addModule(blobUrl);
    } finally {
      URL.revokeObjectURL(blobUrl);
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        autoGainControl: false,
        noiseSuppression: false,
        channelCount: 1,
        sampleRate: 48000,
      },
    });

    const track = stream.getAudioTracks()[0];
    if (!track) {
      stream.getTracks().forEach((t) => t.stop());
      throw new Error('no_audio_track'); // connect() closes the context
    }
    const settings = track.getSettings();
    const applied: AppliedAudioConstraints = {
      echoCancellation: settings.echoCancellation === true,
      autoGainControl: settings.autoGainControl === true,
      noiseSuppression: settings.noiseSuppression === true,
    };

    const source = ctx.createMediaStreamSource(stream);
    const hpf = ctx.createBiquadFilter();
    hpf.type = 'highpass';
    hpf.frequency.value = this.cfg.highPassHz;
    hpf.Q.value = 0.707;

    const worklet = new AudioWorkletNode(ctx, 'shot-detector', {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: 1,
      channelCountMode: 'explicit',
      processorOptions: {
        kRel: this.cfg.kRel,
        aAbs: this.cfg.aAbs,
        minSplitMs: this.cfg.minSplitMs,
        noiseFloorAlpha: this.cfg.noiseFloorAlpha,
        initialNoiseFloor: this.cfg.initialNoiseFloor,
        rearmRatio: this.cfg.rearmRatio,
      },
    });
    worklet.port.onmessage = (e: MessageEvent<WorkletInMsg>) =>
      this.handleWorkletMessage(e.data);

    source.connect(hpf);
    hpf.connect(worklet);
    // Deliberately not connected to ctx.destination — we don't want the
    // shooter to hear themselves through the phone speaker (and on iOS
    // any simultaneous mic+playback flips the device into voice mode).

    this.ctx = ctx;
    this.mediaStream = stream;
    this.workletNode = worklet;
    this.highPassNode = hpf;
    this.appliedConstraints = applied;
    return { applied };
  }

  async disconnect(): Promise<void> {
    this.cancelTimers();
    if (this.workletNode) {
      try {
        this.workletNode.port.postMessage({ type: 'disarm' });
        this.workletNode.disconnect();
      } catch {
        // Already torn down.
      }
    }
    this.highPassNode?.disconnect();
    this.mediaStream?.getTracks().forEach((t) => t.stop());
    if (this.ctx) {
      try {
        await this.ctx.close();
      } catch {
        // Ignore — already closed.
      }
    }
    this.ctx = null;
    this.mediaStream = null;
    this.workletNode = null;
    this.highPassNode = null;
    this.appliedConstraints = null;
    this.startBeepCtxTime = null;
  }

  // --- Live-tweakable settings --------------------------------------------

  /**
   * Apply a stored calibration. Before `connect()` the values go into the
   * worklet's processorOptions; afterwards they are posted to it.
   */
  applyCalibration(cal: PhoneTimerCalibration): void {
    this.cfg.aAbs = cal.aAbs;
    this.cfg.audioRoundTripMs = cal.audioRoundTripMs;
    this.cfg.initialNoiseFloor = cal.noiseFloor;
    if (this.workletNode) {
      this.workletNode.port.postMessage({
        type: 'config',
        aAbs: cal.aAbs,
      });
      this.workletNode.port.postMessage({
        type: 'reset-noise-floor',
        value: cal.noiseFloor,
      });
    }
  }
  // --- TimerSource contract ------------------------------------------------

  async start(): Promise<void> {
    if (!this.ctx || !this.workletNode) throw new Error('not_connected');
    this.cancelTimers();
    this.startBeepCtxTime = null;
    this._status = { state: 'armed', startedAt: null, shots: [] };
    this.emit();

    const delayMs =
      this.cfg.startDelayMode === 'random'
        ? 1000 + Math.random() * 3000
        : this.cfg.startDelayMs;
    this.armDelayTimer = window.setTimeout(() => {
      this.armDelayTimer = null;
      this.fireStartBeep();
    }, delayMs);
  }

  async stop(): Promise<void> {
    this.cancelTimers();
    this.workletNode?.port.postMessage({ type: 'disarm' });
    if (this._status.state === 'idle') return;
    this._status = { ...this._status, state: 'stopped' };
    this.emit();
  }

  reset(): void {
    this.cancelTimers();
    this.workletNode?.port.postMessage({ type: 'disarm' });
    this.startBeepCtxTime = null;
    this._status = { state: 'idle', startedAt: null, shots: [] };
    this.emit();
  }

  on(listener: TimerListener): () => void {
    this.listeners.add(listener);
    listener(this._status);
    return () => this.listeners.delete(listener);
  }

  status(): TimerStatus {
    return this._status;
  }

  /** Subscribe to ~30 Hz level updates for the calibration / VU UI. */
  onLevel(listener: (level: PhoneTimerLevel) => void): () => void {
    this.levelListeners.add(listener);
    return () => this.levelListeners.delete(listener);
  }

  /**
   * Calibration helper. Plays a short tone through the speaker; returns
   * the AudioContext time at which the tone was scheduled. Combine with
   * `onLevel` to find when the peak comes back through the mic and
   * derive speaker→mic round-trip latency. The detector is left
   * untouched (still disarmed); only the level reports are needed.
   */
  playCalibrationTone(opts: { durMs?: number; freqHz?: number; gain?: number } = {}): {
    scheduledAtCtxTime: number;
    durMs: number;
  } | null {
    if (!this.ctx) return null;
    const ctx = this.ctx;
    const dur = (opts.durMs ?? 50) / 1000;
    const freq = opts.freqHz ?? 1000;
    const peak = opts.gain ?? 0.6;
    const start = ctx.currentTime + 0.05;
    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();
    osc.frequency.value = freq;
    osc.type = 'sine';
    gainNode.gain.setValueAtTime(0.0001, start);
    gainNode.gain.exponentialRampToValueAtTime(peak, start + 0.005);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    osc.connect(gainNode).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + dur + 0.02);
    return { scheduledAtCtxTime: start, durMs: opts.durMs ?? 50 };
  }

  // --- internals -----------------------------------------------------------

  private fireStartBeep(): void {
    if (!this.ctx || !this.workletNode) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const beepDur = 0.4;
    const armAfterMs = beepDur * 1000 + this.cfg.preBeepArmDelayMs;

    // Arm first, with detection AND noise-floor learning held off until
    // the beep is over — the beep reaching the mic must neither trigger
    // nor inflate the floor. The worklet switches on audio-clock time.
    this.workletNode.port.postMessage({ type: 'arm', atTime: now + armAfterMs / 1000 });

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 1000;
    osc.type = 'sine';
    // Shaped envelope so the beep itself doesn't trigger the detector
    // through device crosstalk: fast attack, fast decay.
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.6, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + beepDur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + beepDur + 0.05);

    this.startBeepCtxTime = now;

    this.armWorkletTimer = window.setTimeout(() => {
      this.armWorkletTimer = null;
      if (!this.workletNode) return;
      this._status = { ...this._status, state: 'running', startedAt: Date.now() };
      this.emit();

      if (this.cfg.timeLimitMs > 0) {
        this.timeLimitTimer = window.setTimeout(() => {
          this.timeLimitTimer = null;
          void this.stop();
        }, this.cfg.timeLimitMs);
      }
    }, armAfterMs);
  }

  private handleWorkletMessage(msg: WorkletInMsg): void {
    if (msg.type === 'level') {
      const threshold = Math.max(msg.noiseFloor * this.cfg.kRel, this.cfg.aAbs);
      const level: PhoneTimerLevel = {
        rms: msg.rms,
        noiseFloor: msg.noiseFloor,
        threshold,
        t: msg.t,
        peakRms: msg.peakRms,
        peakT: msg.peakT,
      };
      for (const l of this.levelListeners) l(level);
      return;
    }
    if (msg.type === 'shot') {
      if (this.startBeepCtxTime == null) return;
      // A shot message already in flight when stop()/reset() ran must not
      // land on a finished run ('armed' is fine: the main-thread 'running'
      // flip is a timer that can lag the worklet's own arm time).
      if (this._status.state !== 'armed' && this._status.state !== 'running') return;
      const tFromStartMs = Math.max(
        0,
        Math.round((msg.t - this.startBeepCtxTime) * 1000 - this.cfg.audioRoundTripMs),
      );
      const last = this._status.shots[this._status.shots.length - 1];
      const ev: TimerShotEvent = {
        ord: this._status.shots.length + 1,
        tFromStartMs,
        ...(last ? { splitMs: tFromStartMs - last.tFromStartMs } : {}),
      };
      this._status = { ...this._status, shots: [...this._status.shots, ev] };
      this.emit();
      if (
        this.cfg.expectedShots != null &&
        this._status.shots.length >= this.cfg.expectedShots
      ) {
        void this.stop();
      }
    }
  }

  private cancelTimers(): void {
    if (this.armDelayTimer != null) {
      clearTimeout(this.armDelayTimer);
      this.armDelayTimer = null;
    }
    if (this.armWorkletTimer != null) {
      clearTimeout(this.armWorkletTimer);
      this.armWorkletTimer = null;
    }
    if (this.timeLimitTimer != null) {
      clearTimeout(this.timeLimitTimer);
      this.timeLimitTimer = null;
    }
  }

  private emit(): void {
    for (const l of this.listeners) l(this._status);
  }
}

function defaultApplied(): AppliedAudioConstraints {
  return { echoCancellation: false, autoGainControl: false, noiseSuppression: false };
}
